/**
 * 对组件进行校验
 * @param {Object} ref - 组件的引用对象，预期包含 handleValidation 方法
 * @returns {Promise<Object|boolean>} - 返回一个 Promise，校验成功时 resolve 包含 valid 属性的对象，失败时 resolve false 或 reject 错误对象
 */
export function proValidation (ref) {
  // 返回一个 Promise 用于处理异步校验操作
  return new Promise((resolve, reject) => {
    // 检查 ref 是否存在以及是否包含 handleValidation 方法
    if (!ref || !ref.handleValidation) {
      // 若 ref 不合法，拒绝 Promise 并抛出错误
      reject(new Error('ref is not a valid object'))
      return
    }
    // 调用 ref 的 handleValidation 方法进行校验
    ref.handleValidation().then((valid) => {
      // 校验成功，将包含 valid 属性的对象作为结果 resolve
      resolve(valid)
    }).catch((error) => {
      // 捕获校验过程中出现的错误并打印日志
      console.log('表单校验失败', error)
      // 校验失败，将 false 作为结果 resolve
      resolve(false)
    })
  })
}

/**
 * 对传入的 Promise 形式的表单校验操作进行封装处理
 * @param {Promise} promise - 一个 Promise 对象，代表表单校验操作，其 resolve 值应为布尔类型，表示校验是否通过
 * @returns {Promise<boolean>} - 返回一个新的 Promise 对象，根据传入 Promise 的执行结果 resolve 布尔值
 */
export function formValidation (promise) {
  return new Promise((resolve) => {
    promise.then((valid) => {
      if (valid) {
        // 若为真值，将新 Promise 以 true 进行 resolve
        resolve(true)
      } else {
        // 若为假值，将新 Promise 以 false 进行 resolve
        resolve(false)
      }
    // 调用传入 Promise 的 catch 方法，当传入 Promise 被 reject 时执行回调
    }).catch((error) => {
      console.log('表单校验失败', error)
      resolve(false)
    })
  })
}

/**
 * 范围校验
 * @param {Object} rule - 校验规则对象，包含 min 和 max 属性
 * @param {Object} value - 校验值对象，包含 min 和 max 属性
 * @param {Function} callback - 回调函数，用于通知校验结果
 */
export const rangeValidate = (rule, value, callback) => {
  // 解构 value 对象，提供默认值
  const { min = '', max = '' } = value || {}
  // 检查 min 和 max 是否为空或未定义
  if (!min || !max) {
    return callback(new Error('请输入范围数据'))
  }
  // 检查 min 是否大于等于 max
  if (min > max || min === max) {
    return callback(new Error('最小值不能大于等于最大值'))
  }
  // 校验通过
  callback()
}

/**
 * 验证输入值是否在40到80之间的整数
 *
 * @param rule 验证规则
 * @param value 待验证的值
 * @param callback 回调函数，用于处理验证结果
 */
export const hzValidate = (rule, value, callback) => {
  // 只能输入大于等于40 小于等于 80 的整数
  if (!value || value < 40 || value > 80 || value % 1 !== 0) {
    return callback(new Error('请输入40-80之间的整数'))
  }
  callback()
}

/**
 * 验证数值是否符合特定格式（如 0.1、0.01、0.001 等）
 *
 * @param rule 校验规则
 * @param value 需要校验的数值
 * @param callback 校验完成后的回调函数，若校验失败，则传入错误信息
 */
export const decimalValidate = (rule, value, callback) => {
  if (value) {
    const regex = /^0\.0*1$/
    if (!regex.test(value)) {
      callback(new Error('格式必须为 0.1、0.01等'))
    } else {
      callback()
    }
  } else {
    callback()
  }
}
/**
 * 验证当前值是否有效
 *
 * @param {Object} rule 校验规则
 * @param {any} value 待校验的值
 * @param {Function} callback 回调函数，用于处理校验结果
 * @returns {void}
 */
export const currentValueValidate = (rule, value, callback) => {
  // 检查是否为空
  if (!value && value !== 0) {
    return callback(new Error('请输入0-100之间的数字'))
  }

  // 转换为数字并检查是否为有效数字
  const numValue = parseFloat(value)
  if (isNaN(numValue)) {
    return callback(new Error('请输入有效的数字'))
  }

  // 检查是否在0-100范围内
  if (numValue < 0 || numValue > 100) {
    return callback(new Error('请输入0-100之间的数字'))
  }

  // 检查小数位数
  const decimalPart = value.toString().split('.')[1]
  if (decimalPart && decimalPart.length > 2) {
    return callback(new Error('最多保留两位小数'))
  }

  // 验证通过
  callback()
}
/**
 * 验证电压值是否合法
 *
 * @param rule 验证规则
 * @param value 待验证的电压值
 * @param callback 回调函数，验证通过时调用，验证失败时传入错误信息
 */
export const voltageValueValidate = (rule, value, callback) => {
  // 检查是否为空
  if (!value && value !== 0) {
    return callback(new Error('请输入0-380之间的数字'))
  }

  // 转换为数字并检查是否为有效数字
  const numValue = parseFloat(value)
  if (isNaN(numValue)) {
    return callback(new Error('请输入有效的数字'))
  }

  // 检查是否在0-100范围内
  if (numValue < 0 || numValue > 380) {
    return callback(new Error('请输入0-380之间的数字'))
  }

  // 检查小数位数
  const decimalPart = value.toString().split('.')[1]
  if (decimalPart && decimalPart.length > 2) {
    return callback(new Error('最多保留两位小数'))
  }

  // 验证通过
  callback()
}
